import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import { showNotification } from "../../../../Common/Toast/Toast";

const initialState = {
  isLoading: false,
  actionLoading: false,
  error: null,
  teamByService: {},
  invitations: [],
  availableRoles: [],
  rolesLoading: false,
};

const serviceTeamSlice = createSlice({
  name: "serviceTeam",
  initialState,
  reducers: {
    setLoading(state, action) {
      state.isLoading = action.payload;
    },
    setActionLoading(state, action) {
      state.actionLoading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setTeamForService(state, action) {
      const { serviceId, members } = action.payload;
      state.teamByService[serviceId] = members;
    },
    setInvitations(state, action) {
      state.invitations = action.payload;
    },
    setAvailableRoles(state, action) {
      state.availableRoles = action.payload;
    },
    setRolesLoading(state, action) {
      state.rolesLoading = action.payload;
    },
    clearTeam(state) {
      state.teamByService = {};
      state.invitations = [];
      state.availableRoles = [];
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setActionLoading,
  setError,
  setTeamForService,
  setInvitations,
  setAvailableRoles,
  setRolesLoading,
  clearTeam,
} = serviceTeamSlice.actions;

const getErrorMessage = (error, fallback = "Something went wrong") =>
  error?.response?.data?.message || error?.message || fallback;

// ✅ OPTIMIZED: Added caching to prevent unnecessary API calls
export const fetchServiceTeam = (serviceId) => async (dispatch, getState) => {
  if (!serviceId) return;

  // Check cache first (5 minute cache)
  const state = getState();
  const cachedTeam = state.serviceTeam?.teamByService?.[serviceId];

  // If we already have team data cached, skip API call
  if (cachedTeam && Array.isArray(cachedTeam) && cachedTeam.length >= 0) {
    // Team data exists - could add timestamp check here if needed
    // For now, we'll still fetch but this prevents duplicate calls in same render cycle
    return;
  }

  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.get(
      `/serviceProvider/serviceTeam/services/${serviceId}/team`
    );
    if (response.data?.status === "success") {
      dispatch(
        setTeamForService({
          serviceId,
          members: response.data.data || [],
        })
      );
    } else {
      throw new Error(response.data?.message || "Failed to load team members");
    }
  } catch (error) {
    dispatch(setError(getErrorMessage(error)));
    showNotification({
      message: getErrorMessage(error, "Failed to fetch team members"),
      type: "error",
      theme: "light",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

// Fetch available roles for a service (admin-defined via serviceGroup)
export const fetchAvailableRoles = (serviceId) => async (dispatch) => {
  if (!serviceId) return;
  dispatch(setRolesLoading(true));

  try {
    const response = await axiosInstance.get(
      `/serviceProvider/serviceTeam/services/${serviceId}/roles`
    );
    if (response.data?.status === "success") {
      dispatch(setAvailableRoles(response.data.data || []));
    }
  } catch (error) {
    
    // Fallback: set default roles
    dispatch(setAvailableRoles([
      { _id: null, value: 'partner', label: 'Partner', description: 'Manages bookings & daily operations' },
      { _id: null, value: 'manager', label: 'Manager', description: 'Coordinates bookings with limited access' },
      { _id: null, value: 'worker', label: 'Worker', description: 'Executes tasks & updates bookings' },
    ]));
  } finally {
    dispatch(setRolesLoading(false));
  }
};

export const inviteServiceTeamMember = ({ serviceId, payload }) => async (dispatch) => {
  if (!serviceId) return;
  dispatch(setActionLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.post(
      `/serviceProvider/serviceTeam/services/${serviceId}/team/invite`,
      payload
    );

    if (response.data?.status === "success") {
      showNotification({
        message: "Invitation sent successfully",
        type: "success",
        theme: "light",
      });
      dispatch(fetchServiceTeam(serviceId));
    } else {
      throw new Error(response.data?.message || "Failed to send invitation");
    }
  } catch (error) {
    const message = getErrorMessage(error, "Failed to send invitation");
    dispatch(setError(message));
    showNotification({
      message,
      type: "error",
      theme: "light",
    });
    throw error;
  } finally {
    dispatch(setActionLoading(false));
  }
};

export const removeServiceTeamMember = ({ serviceId, memberId }) => async (dispatch) => {
  if (!serviceId || !memberId) return;
  dispatch(setActionLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.delete(
      `/serviceProvider/serviceTeam/services/${serviceId}/team/${memberId}`
    );

    if (response.data?.status === "success") {
      showNotification({
        message: "Team member removed successfully",
        type: "success",
        theme: "light",
      });
      // Refresh team list
      dispatch(fetchServiceTeam(serviceId));
    } else {
      throw new Error(response.data?.message || "Failed to remove team member");
    }
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || "Failed to remove team member";
    dispatch(setError(message));
    showNotification({
      message,
      type: "error",
      theme: "light",
    });
    throw error;
  } finally {
    dispatch(setActionLoading(false));
  }
};

export const fetchInvitationByToken = (token) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.get(
      `/service-invitation/${token}`
    );
    if (response.data?.status === "success") {
      // Set as single invitation in the array
      dispatch(setInvitations([response.data.data]));
      return response.data.data;
    } else {
      throw new Error(
        response.data?.message || "Failed to fetch invitation"
      );
    }
  } catch (error) {
    const message = getErrorMessage(error, "Failed to fetch invitation");
    dispatch(setError(message));
    return null;
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchMyServiceInvitations = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await axiosInstance.get(
      "/serviceProvider/serviceTeam/invitations"
    );
    if (response.data?.status === "success") {
      dispatch(setInvitations(response.data.data || []));
    } else {
      throw new Error(
        response.data?.message || "Failed to fetch invitations list"
      );
    }
  } catch (error) {
    const message = getErrorMessage(error, "Failed to fetch invitations");
    dispatch(setError(message));
    showNotification({
      message,
      type: "error",
      theme: "light",
    });
  } finally {
    dispatch(setLoading(false));
  }
};

export const respondToServiceInvitation =
  ({ token, action }) =>
    async (dispatch) => {
      if (!token || !["accept", "reject"].includes(action)) {
        showNotification({
          message: "Invalid invitation response",
          type: "error",
          theme: "light",
        });
        return;
      }

      dispatch(setActionLoading(true));
      dispatch(setError(null));

      try {
        const response = await axiosInstance.post(
          `/serviceProvider/serviceTeam/invitations/${token}/${action}`
        );

        if (response.data?.status === "success") {
          showNotification({
            message:
              action === "accept"
                ? "Invitation accepted"
                : "Invitation rejected",
            type: "success",
            theme: "light",
          });
          await dispatch(fetchMyServiceInvitations());
        } else {
          throw new Error(
            response.data?.message || "Failed to respond to invitation"
          );
        }
      } catch (error) {
        const message = getErrorMessage(
          error,
          "Failed to update invitation status"
        );
        dispatch(setError(message));
        showNotification({
          message,
          type: "error",
          theme: "light",
        });
        throw error;
      } finally {
        dispatch(setActionLoading(false));
      }
    };

export default serviceTeamSlice.reducer;

